import { useEffect } from 'react';

import { Spinner } from 'design-system';

import { useFactsheetDetails } from '@/features/factsheet/api/queries';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';

import FactsheetBody from './FactsheetBody';
import FactsheetHeader from './FactsheetHeader';

interface FactsheetWindowProps {
  factsheetKey: string;
  openNewTab: (code: string) => void;
  updateTabName: (code: string, newName: string) => void;
}

const ErrorState = () => (
  <div className='flex h-full flex-grow flex-col items-center justify-center'>
    <p className='mt-6 mb-2 text-3xl font-bold'>Factsheet not found</p>
    <p className='text-2xl'>We couldn&apos;t find a factsheet with that code.</p>
  </div>
);

const LoadingState = () => (
  <div className='flex h-full flex-grow items-center justify-center'>
    <Spinner />
  </div>
);

const FactsheetWindow = ({ factsheetKey, openNewTab, updateTabName }: FactsheetWindowProps) => {
  const { data: factsheetDetails, isLoading, isError } = useFactsheetDetails(factsheetKey);
  const isCompact = useIsFactsheetCompact();

  useEffect(() => {
    if (factsheetDetails) {
      updateTabName(factsheetKey, factsheetDetails.code);
    }
  }, [factsheetDetails, factsheetKey, updateTabName]);

  useEffect(() => {
    if (isError) {
      updateTabName(factsheetKey, factsheetKey);
    }
  }, [isError, factsheetKey, updateTabName]);

  if (isError) {
    return <ErrorState />;
  }

  if (isLoading || !factsheetDetails) {
    return <LoadingState />;
  }

  return (
    <div
      className={`flex h-full w-full flex-grow flex-col items-start ${isCompact ? 'overflow-auto' : 'overflow-hidden'}`}
    >
      <FactsheetHeader factsheet={factsheetDetails} />
      <FactsheetBody
        factsheet={factsheetDetails}
        openNewTab={openNewTab}
      />
    </div>
  );
};

export default FactsheetWindow;
